'use client'

import { useState, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faTv, 
  faCircle, 
  faFutbol, 
  faBasketballBall, 
  faGamepad,
  faCalendarDay 
} from '@fortawesome/free-solid-svg-icons'
import LiveMatchCard from './LiveMatchCard'
import { useMatches } from '@/hooks/useDatabase'
import { MatchFilters } from '@/lib/database'

const filters = [
  { icon: faTv, text: 'Tất cả', type: 'liveAll' as const, isActive: true },
  { icon: faCircle, text: 'Đang live', type: 'isLive' as const },
  { icon: faFutbol, text: 'Bóng đá', type: 'liveFootball' as const },
  { icon: faBasketballBall, text: 'Bóng rổ', type: 'liveBasketball' as const },
  { icon: faGamepad, text: 'Esport', type: 'liveEsport' as const },
  { icon: faCalendarDay, text: 'Hôm nay', type: 'liveToday' as const },
]

export default function MatchList() {
  const [activeFilter, setActiveFilter] = useState<MatchFilters['type']>('liveAll')
  
  // Memoize filters object để tránh re-render
  const matchFilters = useMemo(() => ({
    type: activeFilter,
    limit: 12
  }), [activeFilter])
  
  const { matches, loading, error, refetch, loadMore, hasMore } = useMatches(matchFilters)

  const handleFilterChange = (filterType: MatchFilters['type']) => {
    setActiveFilter(filterType)
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500 mb-4">Lỗi tải dữ liệu: {error}</p>
        <button 
          onClick={refetch}
          className="bg-[var(--button-primary)] text-white px-4 py-2 rounded hover:bg-[color-mix(in_srgb,var(--button-primary),#000_10%)]"
        >
          Thử lại
        </button>
      </div>
    )
  }

  return (
    <div className="mb-8" data-testid="match-list">
      {/* Filter Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto p-4 mb-4 bg-[#2a2a2a] rounded-lg border border-white/5 hide-scrollbar">
        {filters.map((filter) => (
          <button
            key={filter.type}
            onClick={() => handleFilterChange(filter.type)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              activeFilter === filter.type
                ? 'bg-[var(--button-primary)] text-white'
                : 'bg-black/20 text-[#999] hover:bg-white/5 hover:text-white'
            }`}
          >
            <FontAwesomeIcon 
              icon={filter.icon} 
              className={`text-sm ${
                filter.type === 'isLive' ? 'text-red-500' : ''
              }`} 
            />
            {filter.text}
          </button>
        ))}
      </div>

      {/* Matches Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && matches.length === 0 ? (
          // Loading skeleton
          [...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#2a2a2a] rounded-lg overflow-hidden border border-white/5 animate-pulse">
              <div className="p-4 border-b border-white/10">
                <div className="flex justify-between items-center">
                  <div className="h-4 bg-gray-600 rounded w-24"></div>
                  <div className="h-6 bg-gray-600 rounded w-16"></div>
                </div>
              </div>
              <div className="p-4 border-b border-white/10">
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
                  <div className="h-4 bg-gray-600 rounded"></div>
                  <div className="flex items-center gap-2">
                    <div className="h-6 bg-gray-600 rounded w-6"></div>
                    <span className="text-[#999]">-</span>
                    <div className="h-6 bg-gray-600 rounded w-6"></div>
                  </div>
                  <div className="h-4 bg-gray-600 rounded"></div>
                </div>
              </div>
              <div className="p-4">
                <div className="h-4 bg-gray-600 rounded w-32"></div>
              </div>
              <div className="p-4">
                <div className="h-8 bg-gray-600 rounded"></div>
              </div>
            </div>
          ))
        ) : matches.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-400">
            Không có trận đấu nào
          </div>
        ) : (
          matches.map((match) => (
            <LiveMatchCard 
              key={match.id} 
              match={match}
              isLoaded={true}
            />
          ))
        )}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center mt-6">
          <button
            onClick={loadMore}
            disabled={loading}
            className="bg-[var(--button-primary)] text-white px-6 py-3 rounded-lg hover:bg-[color-mix(in_srgb,var(--button-primary),#000_10%)] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Đang tải...' : 'Xem thêm trận đấu'}
          </button>
        </div>
      )}

      {/* Loading indicator for load more */}
      {loading && matches.length > 0 && (
        <div className="text-center mt-4">
          <div className="inline-flex items-center gap-2 text-[var(--primary)]">
            <div className="w-4 h-4 border-2 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
            <span>Đang tải thêm...</span>
          </div>
        </div>
      )}
    </div>
  )
} 